import { FormControl, InputAdornment, InputLabel, MenuItem, Select } from "@material-ui/core";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import Switch from "@material-ui/core/Switch";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { green, grey, red } from "@material-ui/core/colors";
import {
   Theme,
   createStyles,
   makeStyles,
   useTheme,
   // eslint-disable-next-line prettier/prettier
   withStyles,
} from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DownloadIcon from "@material-ui/icons/GetApp";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import PublishIcon from "@material-ui/icons/Publish";
import SaveIcon from "@material-ui/icons/Save";
import Alert from "@material-ui/lab/Alert";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
   getCompanyById,
   getCompanyList,
   getPacketLastModifiedDate,
   setCompany,
   // eslint-disable-next-line prettier/prettier
   setDocument,
} from "../../api/CompanyApi";
import ContactDialog from "../../components/ContactDialog/ContactDialog";
import DeleteContactDialog from "../../components/DeleteDialog/DeleteContactDialog";
import DeleteDialog from "../../components/DeleteDialog/DeleteDialog";
import DenialCodeDialog from "../../components/DenialCodeDialog/DenialCodeDialog";
import UploadDialog from "../../components/UploadDialog/UploadDialog";
import { CompanyContext } from "../../stores/Companies/Context";
import { CompanyTypes } from "../../stores/Companies/Reducers";
import { DivisionContext } from "../../stores/Divisions/Context";
import { DrawerContext } from "../../stores/Drawer/Context";
import { UserContext } from "../../stores/Users/Context";
import CheckAnimation from "../../styles/CheckAnimation";
import { Companies } from "../../types/Companies";
import { Contacts } from "../../types/Contacts";
import { DenialCodes } from "../../types/DenialCodes";
import { Divisions } from "../../types/Divisions";
import { DateFormatter } from "../../utilities/DateFormatter";
import "./CompanyDetails.css";

const useStyles = makeStyles((theme: Theme) =>
   createStyles({
      headerDrawerOpen: {
         marginLeft: 240,
      },
      headerDrawerClosed: {
         marginLeft: theme.spacing(7),
      },
      divider: {
         backgroundColor: "#778BA3",
         height: "3px",
         width: "100%",
      },
      activeChip: {
         backgroundColor: green[500],
         color: "white",
      },
      addNewButton: {
         color: "white",
         backgroundColor: green[500],
         "&:hover": {
            backgroundColor: green[600],
         },
      },
      inactiveChip: {
         backgroundColor: red[500],
         color: "white",
      },
      label: {
         flexDirection: "column",
         fontSize: "11px",
      },
      labelMobile: {
         flexDirection: "column",
         fontSize: "7px",
      },
      buttonProgress: {
         color: green[500],
      },
   }),
);

const GreySwitch = withStyles({
   switchBase: {
      color: grey[300],
      "&$checked": {
         color: grey[500],
      },
      "&$checked + $track": {
         backgroundColor: grey[500],
      },
   },
   checked: {},
   track: {},
})(Switch);

const ContactSwitch = withStyles({
   switchBase: {
      color: grey[500],
      "&$checked": {
         color: "#002D72",
      },
      "&$checked + $track": {
         backgroundColor: "#002D72",
      },
   },
   checked: {},
   track: { backgroundColor: grey[400] },
})(Switch);

export const CompanyDetails: React.FC<any> = ({ match }: RouteComponentProps<{ id: string }>): JSX.Element => {
   const classes = useStyles();
   const history = useHistory();
   const theme = useTheme();
   const handleError = useErrorHandler();
   const mobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
   const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));
   const largeScreen = useMediaQuery(theme.breakpoints.down("lg"));

   const { state: drawerState } = useContext(DrawerContext);
   const [selectedCompany, setSelectedCompany] = useState<Companies>({
      _id: "",
      code: "",
      name: "",
      managers: [],
      active: false,
      prefix: "",
      counter: 0,
      addDate: new Date(),
      deleteDate: new Date(),
      invitationBody: "",
      invitationTopNote: "",
      emailTemplate: "",
      docuSignTemplateId: "",
      receiptFirstParagraph: "",
      receiptSecondParagraph: "",
      deniedFirstParagraph: "",
      deniedSecondParagraph: "",
      approvedFirstParagraph: "",
      approvedSecondParagraph: "",
      approvedBoldParagraph: "",
      contactApprovedBody: "",
      contactDeniedBody: "",
      duplicationNotification: "",
      contactDuplicationNotification: "",
      denialCodes: [],
      contacts: [],
      quickCreditAmount: 0,
   });
   const [alertMessage, setAlertMessage] = useState<string>("");
   const [alertType, setAlertType] = useState<"error" | "info" | "success" | "warning">("warning");
   const { state: companyState, dispatch: companyDispatch } = useContext(CompanyContext);
   const { state: divisionState } = useContext(DivisionContext);
   const { state: userState } = useContext(UserContext);
   const [divisionList, setDivisionList] = useState<Divisions[]>([]);
   const [leftList, setLeftList] = React.useState<{ _id: string; value: string }[]>([]);
   const [rightList, setRightList] = React.useState<{ _id: string; value: string }[]>([]);
   const [applicationCounts, setApplicationCounts] = useState<{ item1: string; item2: number }[]>([]);
   const [buttonClicked, setButtonClicked] = useState<boolean>(false);
   const [uploadClicked, setUploadClicked] = useState<boolean>(false);
   const [transferDisabled, setTransferDisabled] = useState<boolean>(false);
   const [docuSignTemplateAssigned, setDocuSignTemplateAssigned] = useState<boolean>(false);
   const [denialCodeModal, setDenialCodeModal] = useState<boolean>(false);
   const [deleteModal, setDeleteModal] = useState<boolean>(false);
   const [contactModal, setContactModal] = useState<boolean>(false);
   const [deleteContactModal, setDeleteContactModal] = useState<boolean>(false);
   const [denialCodeId, setDenialCodeId] = useState<string>("");
   const [contactId, setContactId] = useState<string>("");
   const [selectedDenialCode, setSelectedDenialCode] = useState<DenialCodes>({
      _id: "",
      code: "",
      description: "",
      addDate: null,
   });
   const [selectedContact, setSelectedContact] = useState<Contacts>({
      _id: "",
      name: "",
      email: "",
      active: false,
      addDate: null,
   });
   const [loading, setLoading] = useState(true);
   const [expanded, setExpanded] = useState({
      panel1: true,
      panel2: true,
      panel3: true,
      panel4: true,
      panel5: true,
      panel6: true,
      panel7: true,
      panel8: true,
      "panel-customer-activation-email-1": true,
      "panel-customer-activation-email-2": true,
      "panel-customer-activation-email-3": true,
      "panel-customer-activation-email-4": true,
      "panel-customer-activation-email-5": true,
   });

   const [fileName, setFileName] = useState("");
   const [fileUpload, setFileUpload] = useState<File | null>();
   const [fileUploadModal, setFileUploadModal] = useState<boolean>(false);
   const [fileDate, setFileDate] = useState<Date | null>(null);

   function breadcrumbOnClick(path: string) {
      if (path) {
         history.push(path);
      }
   }

   async function getCompany() {
      try {
         const responseData = await getCompanyById(match.params.id, true);

         if (responseData.success) {
            setSelectedCompany(responseData.data);
            setApplicationCounts(responseData.applicationCount);
         }
      } catch (err) {
         handleError(err);
      }
   }

   useEffect(() => {
      getCompany();
   }, []);

   useEffect(() => {
      if (selectedCompany._id && loading) {
         if (selectedCompany.docuSignTemplateId) {
            setDocuSignTemplateAssigned(true);
         }

         setLoading(false);
      }
   }, [selectedCompany]);

   //States Use Effect.
   useEffect(() => {
      //Merge these for a clean transfer list.
      if (selectedCompany._id !== "" && divisionState.Divisions.length > 0) {
         let newLeftList: { _id: string; value: string }[] = [];
         let newRightList: { _id: string; value: string }[] = [];

         divisionState.Divisions.forEach(function (division: Divisions) {
            if (division._id !== "" && division.active === true && division.deleteDate === null) {
               if (division.company_id === selectedCompany._id) {
                  newRightList = [...newRightList, { _id: division._id, value: division.code + " | " + division.name }];
               } else {
                  newLeftList = [...newLeftList, { _id: division._id, value: division.code + " | " + division.name }];
               }
            }
         });

         newLeftList = _.orderBy(newLeftList, ["value"], ["asc"]);
         newRightList = _.orderBy(newRightList, ["value"], ["asc"]);

         setLeftList(newLeftList);
         setRightList(newRightList);
         setTransferDisabled(userState.CurrentUser.security.toUpperCase() === "SUPER ADMIN" ? false : true);
         setLoading(false);
      }
   }, [divisionState, selectedCompany]);

   async function getPacketDate() {
      try {
         const responseData = await getPacketLastModifiedDate(match.params.id);

         if (responseData.success) {
            setFileDate(responseData.data);
         }
      } catch (err) {
         handleError(err);
      }
   }

   async function openAlertMessage(alertMessage: string, alertType: "error" | "info" | "success" | "warning") {
      setAlertMessage(alertMessage);
      setAlertType(alertType);

      if (alertType === "success" || alertType === "warning") {
         setTimeout(function () {
            setAlertMessage("");
         }, 3000);
      }
   }

   async function onChangeActive(event: React.ChangeEvent<HTMLInputElement>) {
      try {
         const companyObj = { ...selectedCompany };
         companyObj.active = event.target.checked;
         setSelectedCompany(companyObj);
      } catch (err) {
         handleError(err);
      }
   }

   async function onSelectChange(
      event: React.ChangeEvent<{
         name?: string | undefined;
         value: unknown;
      }>,
   ) {
      try {
         const companyObj = { ...selectedCompany };
         companyObj.emailTemplate = event.target.value ? (event.target.value as string) : "";
         setSelectedCompany(companyObj);
      } catch (err) {
         handleError(err);
      }
   }

   // eslint-disable-next-line @typescript-eslint/ban-types
   async function accordionOnChange(
      value:
         | "panel1"
         | "panel2"
         | "panel3"
         | "panel4"
         | "panel5"
         | "panel6"
         | "panel7"
         | "panel8"
         | "panel-customer-activation-email-1"
         | "panel-customer-activation-email-2"
         | "panel-customer-activation-email-3"
         | "panel-customer-activation-email-4"
         | "panel-customer-activation-email-5",
   ) {
      const newExpanded = { ...expanded };

      newExpanded[value] = !newExpanded[value];

      setExpanded(newExpanded);
   }

   async function onChange(
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      value:
         | "name"
         | "code"
         | "prefix"
         | "docuSignTemplateId"
         | "invitationBody"
         | "invitationTopNote"
         | "receiptFirstParagraph"
         | "receiptSecondParagraph"
         | "deniedFirstParagraph"
         | "deniedSecondParagraph"
         | "approvedFirstParagraph"
         | "approvedSecondParagraph"
         | "approvedBoldParagraph"
         | "contactApprovedBody"
         | "contactDeniedBody"
         | "duplicationNotification"
         | "contactDuplicationNotification"
         | "quickCreditAmount",
   ) {
      try {
         const companyObj = { ...selectedCompany };
         if (value === "quickCreditAmount") {
            companyObj[value] = Number(event.target.value);
         } else {
            companyObj[value] = event.target.value;
         }

         setSelectedCompany(companyObj);
      } catch (err) {
         handleError(err);
      }
   }

   function handleCustomerActivationEmailTemplateChange(
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      value:
         | "invitationBody"
         | "invitationTopNote"
         | "receiptFirstParagraph"
         | "receiptSecondParagraph"
         | "approvedFirstParagraph"
         | "approvedSecondParagraph"
         | "statusParagraph",
   ) {
      try {
         const companyClone = { ...selectedCompany };

         if (companyClone.customerActivationEmailTemplate) {
            companyClone.customerActivationEmailTemplate[value] = event.target.value;
            setSelectedCompany(companyClone);
         }
      } catch (err) {
         handleError(err);
      }
   }

   async function saveCompany() {
      try {
         const companyCodeCheck = _.filter(
            companyState.Companies,
            (x) => x.code === selectedCompany.code && x._id !== selectedCompany._id,
         );

         if (companyCodeCheck.length > 0) {
            openAlertMessage("Company Code must be unique", "warning");
            return;
         }

         if (userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN") {
            openAlertMessage("Access to save the Company is denied!", "warning");
            return;
         }

         setButtonClicked(true);

         const companyObj = { ...selectedCompany };

         if (selectedCompany.name === "" || selectedCompany.name === null) {
            openAlertMessage("Please enter a Company Name", "warning");
            return;
         }

         if (selectedCompany.code === "" || selectedCompany.code === null) {
            openAlertMessage("Please enter a Company Code", "warning");
            return;
         }

         if (selectedCompany.prefix === "" || selectedCompany.prefix === null) {
            openAlertMessage("Please enter a Prefix", "warning");
            return;
         }

         if (selectedCompany.emailTemplate === "" || selectedCompany.emailTemplate === null) {
            openAlertMessage("Please select a Email Template", "warning");
            return;
         }

         if (selectedCompany.docuSignTemplateId === "" || selectedCompany.docuSignTemplateId === null) {
            openAlertMessage("Please enter the DocuSign Template Id", "warning");
            return;
         }

         if (
            !selectedCompany.customerActivationEmailTemplate ||
            Object.values(selectedCompany.customerActivationEmailTemplate).some((x) => x === "" || x === null)
         ) {
            openAlertMessage("Please fill out all Customer Activation Email Template fields", "warning");
            return;
         }
         const responseData = await setCompany(companyObj);

         if (responseData.success) {
            const responseDataCompany = await getCompanyList();

            if (responseDataCompany.success && responseDataCompany.data) {
               const companies: Companies[] = responseDataCompany.data;

               companyDispatch({
                  type: CompanyTypes.Create,
                  payload: { Companies: companies },
               });
            } else {
               console.error("Unable to refresh the company state.");
            }

            openAlertMessage("Successfully updated Company.", "success");
            setButtonClicked(false);
            getCompany();
         } else {
            openAlertMessage(responseData.message, "warning");
            setButtonClicked(false);
            return;
         }
      } catch (err) {
         setButtonClicked(false);
         handleError(err);
      }
   }

   async function handleDeleteClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) {
      try {
         setSelectedDenialCode(selectedCompany.denialCodes[index]);
         setDeleteModal(true);
      } catch (err) {
         handleError(err);
      }
   }

   async function onChangeActiveContact(event: React.ChangeEvent<HTMLInputElement>, name: string) {
      try {
         const index = _.findIndex(selectedCompany.contacts, (x) => x.name === name);

         if (!event.target.checked) {
            setSelectedContact(selectedCompany.contacts[index]);
            setDeleteContactModal(true);
         } else {
            const companyObj = { ...selectedCompany };
            companyObj.contacts[index].active = event.target.checked;
            setSelectedCompany(companyObj);

            saveCompany();
         }
      } catch (err) {
         handleError(err);
      }
   }

   async function deleteDenialCode() {
      try {
         setDeleteModal(!deleteModal);

         const companyObj = { ...selectedCompany };

         const index = _.findIndex(companyObj.denialCodes, (x) => x.code === selectedDenialCode.code);

         if (index > -1) {
            companyObj.denialCodes.splice(index, 1);
            setSelectedCompany(companyObj);
         }

         saveCompany();
      } catch (err) {
         handleError(err);
      }
   }

   async function deleteContact() {
      try {
         setDeleteContactModal(!deleteContactModal);

         const companyObj = { ...selectedCompany };

         const index = _.findIndex(companyObj.contacts, (x) => x._id === selectedContact._id);

         if (index > -1) {
            companyObj.contacts[index].active = false;
         }

         saveCompany();
      } catch (err) {
         handleError(err);
      }
   }

   async function handleClose() {
      try {
         history.push("/companies");
      } catch (err) {
         handleError(err);
      }
   }

   async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target.files != null) {
         setFileUpload(event.target.files[0]);
         setFileUploadModal(true);
      }
   }

   async function uploadPacket() {
      try {
         setFileUploadModal(false);
         setUploadClicked(true);

         const formData = new FormData();

         if (fileUpload != null) {
            //Set file and set the company id.
            formData.append("OnboardingDocument", fileUpload);
            formData.append("Company_id", selectedCompany._id);

            const responseData = await setDocument(formData);

            if (responseData.success) {
               openAlertMessage("Successfully Uploaded Packet.", "success");
               setUploadClicked(false);
            } else {
               openAlertMessage(responseData.message, "warning");
               setUploadClicked(false);
               return;
            }
         }
      } catch (err) {
         setUploadClicked(false);
         handleError(err);
      }

      getPacketDate();
   }

   async function handleDownloadClick() {
      if (selectedCompany.emailTemplate) {
         switch (selectedCompany.emailTemplate) {
            case "CPG":
            case "IDS":
            default:
               window.open("https://worldgroup.blob.core.windows.net/cpgdocuments/New Customer Packet- Intermodal.pdf");
               break;
            case "MIDDLEBAY":
               window.open(
                  "https://worldgroup.blob.core.windows.net/cpgdocuments/New Customer Packet - Middle Bay.pdf",
               );
               break;
            case "BRISTOL":
               window.open("https://worldgroup.blob.core.windows.net/cpgdocuments/New Customer Packet - Bristol.pdf");
               break;
            case "UWL":
               //COME BACK TO LATER!!!!!
               window.open("https://worldgroup.blob.core.windows.net/cpgdocuments/New Customer Packet - Bristol.pdf");
               break;
            case "WDS":
               //COME BACK TO LATER!!!!!
               window.open("https://worldgroup.blob.core.windows.net/cpgdocuments/New Customer Packet - Bristol.pdf");
               break;
         }
      } else {
         openAlertMessage("New customer packet unavailable for this company!", "warning");
      }
   }

   const OnboardingPacket = () => {
      return (
         <Card style={{ width: "100%", marginTop: theme.spacing(7) }}>
            <CardHeader
               className={"CompanyDetails-card-header1"}
               style={{ height: "4rem" }}
               title={
                  <Typography noWrap variant="h6">
                     {"Onboarding Packet"}
                  </Typography>
               }
            />
            <Divider className={classes.divider} />
            <CardContent>
               <TableContainer>
                  <Table style={{ marginBottom: "1rem" }}>
                     <TableHead>
                        <TableRow>
                           <TableCell style={{ fontSize: "16px", fontWeight: 700 }}> Packet </TableCell>
                           <TableCell style={{ fontSize: "16px", fontWeight: 700 }}> Date Added </TableCell>
                           <TableCell style={{ fontSize: "16px", fontWeight: 700 }} />
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        <TableRow key={`tr-${selectedCompany._id}`}>
                           <TableCell style={{ fontSize: "16px" }}> {fileName} </TableCell>
                           <TableCell style={{ fontSize: "16px" }}>{fileDate ? DateFormatter(fileDate) : ""}</TableCell>
                           <TableCell>
                              <ButtonGroup>
                                 <Tooltip title="Upload" arrow>
                                    <IconButton
                                       component="label"
                                       disabled={userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"}
                                       color="primary"
                                       classes={{
                                          label: mobileScreen ? classes.labelMobile : classes.label,
                                       }}
                                    >
                                       <input type="file" hidden accept=".pdf" onChange={handleFileChange} />

                                       {uploadClicked ? (
                                          <CircularProgress size={16} className={classes.buttonProgress} />
                                       ) : (
                                          <PublishIcon />
                                       )}
                                    </IconButton>
                                 </Tooltip>
                                 <Tooltip title="Download" arrow>
                                    <IconButton
                                       disabled={userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"}
                                       color="secondary"
                                       onClick={() => handleDownloadClick()}
                                    >
                                       <DownloadIcon />
                                    </IconButton>
                                 </Tooltip>
                              </ButtonGroup>
                           </TableCell>
                        </TableRow>
                     </TableBody>
                  </Table>
               </TableContainer>
            </CardContent>
         </Card>
      );
   };

   return (
      <>
         <div className="CompanyDetails">
            <div className="CompanyDetails-root">
               {loading ? (
                  <>
                     <CheckAnimation />
                  </>
               ) : (
                  <>
                     <div className="CompanyDetails-header">
                        <div
                           className={
                              drawerState.DrawerOpen === true ? classes.headerDrawerOpen : classes.headerDrawerClosed
                           }
                        >
                           <Grid
                              xs={12}
                              container
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              style={{ height: "60px" }}
                           >
                              <Grid
                                 container
                                 direction="column"
                                 item
                                 xs={mobileScreen ? 7 : 10}
                                 justifyContent="center"
                                 alignItems="flex-start"
                                 style={{ height: "60px" }}
                              >
                                 <Breadcrumbs separator={<NavigateNextIcon />}>
                                    <Link
                                       color="inherit"
                                       style={{ cursor: "pointer" }}
                                       onClick={() => breadcrumbOnClick("/companies")}
                                    >
                                       <Typography noWrap variant="h6" style={{ fontWeight: 700 }}>
                                          {"Companies"}
                                       </Typography>
                                    </Link>
                                    <Link
                                       color="inherit"
                                       style={{ cursor: "pointer" }}
                                       onClick={() => breadcrumbOnClick("/companies/details/" + match.params.id)}
                                    >
                                       <Typography noWrap variant="h6" style={{ fontWeight: 700 }}>
                                          {selectedCompany.name ? selectedCompany.name : null}
                                       </Typography>
                                    </Link>
                                 </Breadcrumbs>
                              </Grid>
                              <Grid
                                 container
                                 direction="column"
                                 item
                                 xs={mobileScreen ? 5 : 2}
                                 justifyContent="center"
                                 alignItems="center"
                                 style={{ height: "60px" }}
                              >
                                 <ButtonGroup>
                                    <Button
                                       onClick={() => handleClose()}
                                       variant="contained"
                                       classes={{
                                          label: mobileScreen ? classes.labelMobile : classes.label,
                                       }}
                                    >
                                       <CloseIcon />
                                       Close
                                    </Button>
                                    <Button
                                       onClick={() => saveCompany()}
                                       variant="contained"
                                       color="primary"
                                       classes={{
                                          label: mobileScreen ? classes.labelMobile : classes.label,
                                       }}
                                       disabled={
                                          userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN" ||
                                          buttonClicked
                                       }
                                    >
                                       {buttonClicked ? (
                                          <CircularProgress size={20} className={classes.buttonProgress} />
                                       ) : (
                                          <SaveIcon />
                                       )}
                                       {mobileScreen ? "Save" : "Save"}
                                    </Button>
                                 </ButtonGroup>
                              </Grid>
                           </Grid>
                        </div>
                     </div>
                     <div
                        className={
                           drawerState.DrawerOpen === true ? classes.headerDrawerOpen : classes.headerDrawerClosed
                        }
                     >
                        <Collapse in={alertMessage ? true : false}>
                           <Alert
                              variant="filled"
                              severity={alertType}
                              action={
                                 <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                       setAlertMessage("");
                                    }}
                                 >
                                    <CloseIcon />
                                 </IconButton>
                              }
                           >
                              {alertMessage}
                           </Alert>
                        </Collapse>
                        <div className="CompanyDetails-body">
                           <div
                              className={
                                 drawerState.DrawerOpen === true
                                    ? "CompanyDetails-layout-drawer-open"
                                    : "CompanyDetails-layout"
                              }
                           >
                              <Grid container direction={"row"} item xs={12}>
                                 <Grid container direction={"column"} item xs={mobileScreen ? 12 : 6}>
                                    <Card style={{ width: "100%" }}>
                                       <CardHeader
                                          className={"CompanyDetails-card-header1"}
                                          action={
                                             <div>
                                                <FormControlLabel
                                                   style={{ marginTop: ".25rem" }}
                                                   control={
                                                      <GreySwitch
                                                         checked={selectedCompany.active ? true : false}
                                                         onChange={(event) => onChangeActive(event)}
                                                         disabled={
                                                            userState.CurrentUser.security.toUpperCase() !==
                                                            "SUPER ADMIN"
                                                         }
                                                      />
                                                   }
                                                   label={selectedCompany.active ? "Active" : "Inactive"}
                                                   labelPlacement="start"
                                                />
                                             </div>
                                          }
                                          title={
                                             <Typography noWrap variant="h6">
                                                {"Company Information"}
                                             </Typography>
                                          }
                                          style={{ height: "4rem" }}
                                       />
                                       <Divider className={classes.divider} />
                                       <CardContent>
                                          <Grid container direction={"column"} item xs={12} alignItems="center">
                                             <Grid container direction={"row"} item xs={12} alignItems="center">
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-code"
                                                      label="Company Code"
                                                      variant="outlined"
                                                      name="Company Code"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={selectedCompany.code}
                                                      onChange={(event) => onChange(event, "code")}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-name"
                                                      label="Company Name"
                                                      variant="outlined"
                                                      name="name"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={selectedCompany.name}
                                                      onChange={(event) => onChange(event, "name")}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </Grid>

                                             <Grid container direction={"row"} item xs={12} alignItems="center">
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-dateAdded"
                                                      label="Date Added"
                                                      variant="outlined"
                                                      name="dateAdded"
                                                      value={DateFormatter(selectedCompany.addDate)}
                                                      disabled={true}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <FormControl
                                                      variant="outlined"
                                                      style={{ width: "100%", textAlign: "left" }}
                                                   >
                                                      <InputLabel>Email Templates</InputLabel>
                                                      <Select
                                                         id="select-emailtemplate"
                                                         defaultValue=""
                                                         label="Email Template"
                                                         value={selectedCompany.emailTemplate}
                                                         onChange={(event) => onSelectChange(event)}
                                                         disabled={
                                                            userState.CurrentUser.security.toUpperCase() !==
                                                            "SUPER ADMIN"
                                                         }
                                                      >
                                                         <MenuItem value={"CPG"}>{"CPG"}</MenuItem>
                                                         <MenuItem value={"IDS"}>{"IDS"}</MenuItem>
                                                         <MenuItem value={"MIDDLEBAY"}>{"Middlebay"}</MenuItem>
                                                         <MenuItem value={"BRISTOL"}>{"Bristol"}</MenuItem>
                                                         <MenuItem value={"WDS"}>
                                                            {"World Distribution Services"}
                                                         </MenuItem>
                                                         <MenuItem value={"UWL"}>{"UWL, Inc."}</MenuItem>
                                                      </Select>
                                                   </FormControl>
                                                </Grid>
                                             </Grid>
                                             <Grid container direction={"row"} item xs={12} alignItems="center">
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-applicationPrefix"
                                                      label="Application Prefix"
                                                      variant="outlined"
                                                      name="applicationPrefix"
                                                      value={selectedCompany.prefix}
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      onChange={(event) => onChange(event, "prefix")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-applicationCount"
                                                      label="# of Completed Applications"
                                                      variant="outlined"
                                                      name="applicationCount"
                                                      value={applicationCounts
                                                         .filter((x) => x.item1 === selectedCompany._id)
                                                         .map((x) => {
                                                            return x.item2.toString();
                                                         })}
                                                      disabled={true}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </Grid>
                                             <Grid container direction={"row"} item xs={12} alignItems="center">
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-docuSignTemplateId"
                                                      label="DocuSign Template Id"
                                                      variant="outlined"
                                                      name="docuSignTemplateId"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !==
                                                            "SUPER ADMIN" || docuSignTemplateAssigned
                                                      }
                                                      value={selectedCompany.docuSignTemplateId}
                                                      onChange={(event) => onChange(event, "docuSignTemplateId")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid container item xs={mobileScreen ? 12 : 6} alignItems="center">
                                                   <TextField
                                                      id="tf-quickCreditAmount"
                                                      label="Quick Credit Amount"
                                                      variant="outlined"
                                                      name="quickCreditAmount"
                                                      InputProps={{
                                                         startAdornment: (
                                                            <InputAdornment position="start">$</InputAdornment>
                                                         ),
                                                      }}
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={selectedCompany.quickCreditAmount}
                                                      onChange={(event) => onChange(event, "quickCreditAmount")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </Grid>
                                          </Grid>
                                       </CardContent>
                                    </Card>
                                    <Card style={{ width: "100%", marginTop: theme.spacing(7) }}>
                                       <CardHeader
                                          className={"UserDetails-card-header1"}
                                          style={{ height: "4rem" }}
                                          title={
                                             <Typography noWrap variant="h6">
                                                {"Credit Application Email Templates"}
                                             </Typography>
                                          }
                                       />
                                       <Divider className={classes.divider} />

                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel1}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel1")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                             >
                                                <Typography>Invitation Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-invitationBody"
                                                      label="Invitation Body"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      name="invitationBody"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={selectedCompany.invitationBody}
                                                      onChange={(event) => onChange(event, "invitationBody")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-InvitationTopNote"
                                                      label="Invitation Top Note"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      name="invitationTopNote"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={selectedCompany.invitationTopNote}
                                                      onChange={(event) => onChange(event, "invitationTopNote")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel2}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel2")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel2a-content"
                                                id="panel2a-header"
                                             >
                                                <Typography>Receipt Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-ReceiptFirstParagraph"
                                                      label="Receipt First Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="receiptFirstParagraph"
                                                      value={selectedCompany.receiptFirstParagraph}
                                                      onChange={(event) => onChange(event, "receiptFirstParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-ReceiptSecondParagraph"
                                                      label="Receipt Second Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      name="receiptSecondParagraph"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={selectedCompany.receiptSecondParagraph}
                                                      onChange={(event) => onChange(event, "receiptSecondParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel3}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel3")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel3a-content"
                                                id="panel3a-header"
                                             >
                                                <Typography>Denied Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-DeniedFirstParagraph"
                                                      label="Denied First Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="deniedFirstParagraph"
                                                      value={selectedCompany.deniedFirstParagraph}
                                                      onChange={(event) => onChange(event, "deniedFirstParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-DeniedSecondParagraph"
                                                      label="Denied Second Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="deniedSecondParagraph"
                                                      value={selectedCompany.deniedSecondParagraph}
                                                      onChange={(event) => onChange(event, "deniedSecondParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel4}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel4")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel4a-content"
                                                id="panel4a-header"
                                             >
                                                <Typography>Approved Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-ApprovedParagraph"
                                                      label="Approved Bold Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="approvedBoldParagraph"
                                                      value={selectedCompany.approvedBoldParagraph}
                                                      onChange={(event) => onChange(event, "approvedBoldParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-approvedFirstParagraph"
                                                      label="Approved First Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="approvedFirstParagraph"
                                                      value={selectedCompany.approvedFirstParagraph}
                                                      onChange={(event) => onChange(event, "approvedFirstParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-approvedSecondParagraph"
                                                      label="Approved Second Paragraph"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="approvedSecondParagraph"
                                                      value={selectedCompany.approvedSecondParagraph}
                                                      onChange={(event) => onChange(event, "approvedSecondParagraph")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel5}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel5")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel4a-content"
                                                id="panel4a-header"
                                             >
                                                <Typography>Contact Approved Email Template - Internal</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-ContactApproved"
                                                      label="Contact Approved Body"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="contactApprovedBody"
                                                      value={selectedCompany.contactApprovedBody}
                                                      onChange={(event) => onChange(event, "contactApprovedBody")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel6}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel6")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel4a-content"
                                                id="panel4a-header"
                                             >
                                                <Typography>Contact Denied Email Template - Internal</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-ContactDenied"
                                                      label="Contact Denied Body"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="contactDeniedBody"
                                                      value={selectedCompany.contactDeniedBody}
                                                      onChange={(event) => onChange(event, "contactDeniedBody")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel7}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel7")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel4a-content"
                                                id="panel4a-header"
                                             >
                                                <Typography>Duplication Notification - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-DuplicationNotification"
                                                      label="Duplication Notification"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="duplicationNotification"
                                                      value={selectedCompany.duplicationNotification}
                                                      onChange={(event) => onChange(event, "duplicationNotification")}
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded.panel8}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel8")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel4a-content"
                                                id="panel4a-header"
                                             >
                                                <Typography>Duplication Notification - Internal</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="tf-ContactDuplicationNotification"
                                                      label="Contact Duplication Notification"
                                                      multiline
                                                      minRows={4}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="contactDuplicationNotification"
                                                      value={selectedCompany.contactDuplicationNotification}
                                                      onChange={(event) =>
                                                         onChange(event, "contactDuplicationNotification")
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                    </Card>
                                 </Grid>
                                 <Grid container direction={"column"} item xs={mobileScreen ? 12 : 6}>
                                    <Card
                                       style={{
                                          width: "100%",
                                          minHeight: largeScreen || mediumScreen || mobileScreen ? undefined : "477px",
                                       }}
                                    >
                                       <CardHeader
                                          className={"CompanyDetails-card-header1"}
                                          style={{ height: "4rem" }}
                                          action={
                                             <div>
                                                <Button
                                                   onClick={() => {
                                                      setContactModal(true);
                                                      setContactId("");
                                                   }}
                                                   variant="contained"
                                                   classes={{
                                                      label: mobileScreen ? classes.labelMobile : classes.label,
                                                      root: classes.addNewButton,
                                                   }}
                                                   disabled={
                                                      userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                   }
                                                >
                                                   <AddIcon />
                                                   {mobileScreen ? "New" : "Contact"}
                                                </Button>
                                             </div>
                                          }
                                          title={
                                             <Typography noWrap variant="h6">
                                                {"Contact List"}
                                             </Typography>
                                          }
                                       />
                                       <Divider className={classes.divider} />
                                       <CardContent>
                                          <Grid container direction={"column"} xs={12}>
                                             <Grid container direction={"row"} xs={12}>
                                                {_.orderBy(
                                                   selectedCompany.contacts,
                                                   [(contact) => contact.name.toLowerCase()],
                                                   ["asc"],
                                                ).map((contact, index) => {
                                                   return (
                                                      <Grid
                                                         key={"grid=" + contact._id}
                                                         item
                                                         xs={mobileScreen ? 6 : 4}
                                                         style={{ display: "inline-flex", alignItems: "center" }}
                                                      >
                                                         <Grid
                                                            item
                                                            xs={mobileScreen ? 6 : 4}
                                                            style={{ display: "inline-flex", alignItems: "center" }}
                                                         >
                                                            <h4>{contact.name}</h4>
                                                         </Grid>

                                                         <Grid
                                                            item
                                                            xs={mobileScreen ? 6 : 4}
                                                            style={{ display: "inline-flex", alignItems: "center" }}
                                                         >
                                                            <FormControlLabel
                                                               style={{
                                                                  marginRight: "0px",
                                                                  fontStretch: "ultra-condensed",
                                                               }}
                                                               control={
                                                                  <ContactSwitch
                                                                     checked={contact.active ? true : false}
                                                                     onChange={(event) =>
                                                                        onChangeActiveContact(event, contact.name)
                                                                     }
                                                                     disabled={
                                                                        userState.CurrentUser.security.toUpperCase() !==
                                                                        "SUPER ADMIN"
                                                                     }
                                                                  />
                                                               }
                                                               label=""
                                                               labelPlacement="top"
                                                            />
                                                         </Grid>
                                                         <Grid
                                                            item
                                                            xs={mobileScreen ? 6 : 4}
                                                            style={{ display: "inline-flex", alignItems: "center" }}
                                                         >
                                                            <ButtonGroup>
                                                               <Tooltip title="Edit" arrow>
                                                                  <IconButton
                                                                     disabled={
                                                                        userState.CurrentUser.security.toUpperCase() !==
                                                                        "SUPER ADMIN"
                                                                     }
                                                                     color="primary"
                                                                     onClick={() => {
                                                                        setContactModal(true);
                                                                        setContactId(contact._id);
                                                                     }}
                                                                  >
                                                                     <EditIcon />
                                                                  </IconButton>
                                                               </Tooltip>
                                                            </ButtonGroup>
                                                         </Grid>
                                                      </Grid>
                                                   );
                                                })}
                                             </Grid>
                                          </Grid>
                                       </CardContent>
                                    </Card>
                                    <Card style={{ width: "100%", marginTop: theme.spacing(7) }}>
                                       <CardHeader
                                          className={"CompanyDetails-card-header1"}
                                          action={
                                             <div>
                                                <Button
                                                   onClick={() => {
                                                      setDenialCodeModal(true);
                                                      setDenialCodeId("");
                                                   }}
                                                   variant="contained"
                                                   classes={{
                                                      label: mobileScreen ? classes.labelMobile : classes.label,
                                                      root: classes.addNewButton,
                                                   }}
                                                   disabled={
                                                      userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                   }
                                                >
                                                   <AddIcon />
                                                   {mobileScreen ? "New" : "New Denial Code"}
                                                </Button>
                                             </div>
                                          }
                                          title={
                                             <Typography noWrap variant="h6">
                                                {"Denial Codes"}
                                             </Typography>
                                          }
                                          style={{ height: "4rem" }}
                                       />
                                       <Divider className={classes.divider} />
                                       <CardContent>
                                          <TableContainer>
                                             <Table style={{ marginBottom: "1rem" }}>
                                                <TableHead>
                                                   <TableRow>
                                                      <TableCell style={{ fontSize: "16px", fontWeight: 700 }}>
                                                         Code
                                                      </TableCell>
                                                      <TableCell style={{ fontSize: "16px", fontWeight: 700 }}>
                                                         Description
                                                      </TableCell>
                                                      <TableCell style={{ fontSize: "16px", fontWeight: 700 }}>
                                                         Date Added
                                                      </TableCell>
                                                      <TableCell> </TableCell>
                                                   </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                   {selectedCompany.denialCodes.map((dc, index) => {
                                                      return (
                                                         <TableRow key={`tr-${dc._id}`}>
                                                            <TableCell style={{ fontSize: "16px" }}>
                                                               {dc.code}
                                                            </TableCell>
                                                            <TableCell style={{ fontSize: "16px" }}>
                                                               {dc.description}
                                                            </TableCell>
                                                            <TableCell style={{ fontSize: "16px" }}>
                                                               {DateFormatter(dc.addDate)}
                                                            </TableCell>
                                                            <TableCell>
                                                               <ButtonGroup>
                                                                  <Tooltip title="Edit" arrow>
                                                                     <IconButton
                                                                        disabled={
                                                                           userState.CurrentUser.security.toUpperCase() !==
                                                                           "SUPER ADMIN"
                                                                        }
                                                                        color="primary"
                                                                        onClick={() => {
                                                                           setDenialCodeModal(true);
                                                                           setDenialCodeId(dc._id);
                                                                        }}
                                                                     >
                                                                        <EditIcon />
                                                                     </IconButton>
                                                                  </Tooltip>
                                                                  <Tooltip title="Delete" arrow>
                                                                     <IconButton
                                                                        disabled={
                                                                           userState.CurrentUser.security.toUpperCase() !==
                                                                           "SUPER ADMIN"
                                                                        }
                                                                        color="secondary"
                                                                        onClick={(event) =>
                                                                           handleDeleteClick(event, index)
                                                                        }
                                                                     >
                                                                        <DeleteForeverIcon />
                                                                     </IconButton>
                                                                  </Tooltip>
                                                               </ButtonGroup>
                                                            </TableCell>
                                                         </TableRow>
                                                      );
                                                   })}
                                                </TableBody>
                                             </Table>
                                          </TableContainer>
                                       </CardContent>
                                    </Card>
                                    <Card
                                       id="CustomerActivationEmailTemplates"
                                       style={{ width: "100%", marginTop: theme.spacing(7) }}
                                    >
                                       <CardHeader
                                          className={"UserDetails-card-header1"}
                                          style={{ height: "4rem" }}
                                          title={
                                             <Typography noWrap variant="h6">
                                                {"Customer Activation Email Templates"}
                                             </Typography>
                                          }
                                       />
                                       <Divider className={classes.divider} />
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded["panel-customer-activation-email-1"]}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel-customer-activation-email-1")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                             >
                                                <Typography>Invitation Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-invitationBody"
                                                      label="Invitation Body"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      name="invitationBody"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate?.invitationBody
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "invitationBody",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-invitation-top-note"
                                                      label="Invitation Top Note"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="invitationTopNote"
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate
                                                            ?.invitationTopNote
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "invitationTopNote",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded["panel-customer-activation-email-2"]}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel-customer-activation-email-2")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                             >
                                                <Typography>Receipt Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-receipt-first-paragraph"
                                                      label="Receipt First Paragraph"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      name="receiptFirstParapraph"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate
                                                            ?.receiptFirstParagraph
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "receiptFirstParagraph",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-receipt-second-paragraph"
                                                      label="Receipt Second Paragraph"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="receiptSecondParagraph"
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate
                                                            ?.receiptSecondParagraph
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "receiptSecondParagraph",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded["panel-customer-activation-email-3"]}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel-customer-activation-email-3")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                             >
                                                <Typography>Approved Email Template - External</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-approved-first-paragraph"
                                                      label="Approved First Paragraph"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      name="approvedFirstParapraph"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate
                                                            ?.approvedFirstParagraph
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "approvedFirstParagraph",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-approved-second-paragraph"
                                                      label="Approved Second Paragraph"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      name="approvedSecondParagraph"
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate
                                                            ?.approvedSecondParagraph
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "approvedSecondParagraph",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                       <Grid
                                          container
                                          direction={"row"}
                                          item
                                          xs={12}
                                          justifyContent="flex-start"
                                          alignItems="flex-start"
                                       >
                                          <Accordion
                                             expanded={expanded["panel-customer-activation-email-4"]}
                                             className={"CompanyDetails-Accordion"}
                                             onChange={() => accordionOnChange("panel-customer-activation-email-4")}
                                          >
                                             <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                             >
                                                <Typography>Status Email Template - Internal</Typography>
                                             </AccordionSummary>
                                             <AccordionDetails>
                                                <Grid
                                                   container
                                                   direction={"row"}
                                                   item
                                                   xs={12}
                                                   justifyContent="flex-start"
                                                   alignItems="flex-start"
                                                >
                                                   <TextField
                                                      id="customer-activation-status-paragraph"
                                                      label="Status Paragraph"
                                                      multiline
                                                      minRows={2}
                                                      variant="outlined"
                                                      name="statusParapraph"
                                                      disabled={
                                                         userState.CurrentUser.security.toUpperCase() !== "SUPER ADMIN"
                                                      }
                                                      value={
                                                         selectedCompany.customerActivationEmailTemplate
                                                            ?.statusParagraph
                                                      }
                                                      onChange={(event) =>
                                                         handleCustomerActivationEmailTemplateChange(
                                                            event,
                                                            "statusParagraph",
                                                         )
                                                      }
                                                      style={{ marginTop: mobileScreen ? ".5rem" : "0rem" }}
                                                      autoComplete="new-password"
                                                   />
                                                </Grid>
                                             </AccordionDetails>
                                          </Accordion>
                                       </Grid>
                                    </Card>
                                    {/* NOT NEEDED RIGHT NOW! */}
                                    {/* <OnboardingPacket /> */}
                                 </Grid>
                              </Grid>
                           </div>
                        </div>
                     </div>
                  </>
               )}
            </div>
         </div>
         <DenialCodeDialog
            open={denialCodeModal}
            divisionOrCompany={selectedCompany}
            setDivisionOrCompany={setSelectedCompany}
            component={"company"}
            denialCodeId={denialCodeId}
            handleClose={() => setDenialCodeModal(!denialCodeModal)}
            openAlertMessage={openAlertMessage}
         />
         <DeleteDialog
            open={deleteModal}
            handleDelete={() => deleteDenialCode()}
            handleClose={() => setDeleteModal(!deleteModal)}
            title={selectedDenialCode && `Delete ${selectedDenialCode.code}`}
            text={selectedDenialCode && `Are you sure you want to delete ${selectedDenialCode.code}?`}
         />
         <ContactDialog
            open={contactModal}
            divisionOrCompany={selectedCompany}
            setDivisionOrCompany={setSelectedCompany}
            component={"company"}
            contactId={contactId}
            handleClose={() => setContactModal(!contactModal)}
            openAlertMessage={openAlertMessage}
         />
         <DeleteContactDialog
            open={deleteContactModal}
            handleDelete={() => deleteContact()}
            handleClose={() => setDeleteContactModal(!deleteContactModal)}
            title={selectedContact && `Deactivate ${selectedContact.name}`}
            text={
               selectedContact && `You are about to deactivate contact ${selectedContact.name} do you want to continue?`
            }
         />
         <UploadDialog
            open={fileUploadModal}
            handleUpload={() => uploadPacket()}
            handleClose={() => setFileUploadModal(false)}
            title={selectedDenialCode && `Upload ${fileUpload?.name}`}
            text={
               selectedDenialCode &&
               `Are you sure you want to upload the onboarding packet for ${selectedCompany.name}? This will overwrite the current packet.`
            }
         />
      </>
   );
};

export default CompanyDetails;
